import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { currencyService } from '../../services/currency.service';
import {
  type CurrencyCode,
  type FxRateSnapshot,
  FALLBACK_RATE,
  detectCurrencyFromLocale,
  currencyForCountry,
} from '../../types/currency.types';

const LS_KEY = 'ca_display_currency';

function readOverride(): CurrencyCode | null {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v === 'NGN' || v === 'USD') return v;
  } catch { /* SSR / incognito */ }
  return null;
}

interface CurrencyState {
  displayCurrency: CurrencyCode;
  adminDefault: CurrencyCode;
  rate: FxRateSnapshot;
  status: 'idle' | 'loading' | 'ready';
  /** ISO 3166-1 alpha-2 country code from IP geo, or null if unknown. */
  geoCountry: string | null;
  /** True if the user has explicitly overridden the currency. */
  hasUserOverride: boolean;
}

const override = readOverride();

const initialState: CurrencyState = {
  displayCurrency: override ?? detectCurrencyFromLocale(),
  adminDefault: 'USD',
  rate: FALLBACK_RATE,
  status: 'idle',
  geoCountry: null,
  hasUserOverride: !!override,
};

export const fetchCurrencySettings = createAsyncThunk(
  'currency/fetchSettings',
  async () => {
    const settings = await currencyService.getPublicSettings();
    return settings;
  },
);

export const updateDefaultCurrency = createAsyncThunk(
  'currency/updateDefault',
  async (code: CurrencyCode) => {
    const settings = await currencyService.setDefaultCurrency(code);
    if (!settings) throw new Error('Failed to update');
    return settings;
  },
);

/**
 * Detect the visitor's country from their IP and, if they haven't explicitly
 * overridden the currency, apply the geo-based default (NG → NGN, else USD).
 *
 * Precedence enforced:
 *   1. user override (localStorage)  ← always wins, never touched here
 *   2. IP geo result                 ← applied by this thunk
 *   3. admin default                 ← handled by fetchCurrencySettings
 *   4. browser locale                ← initial state seed
 */
export const detectGeoCurrency = createAsyncThunk<
  { country: string | null; applied: CurrencyCode | null },
  void,
  { state: { currency: CurrencyState } }
>(
  'currency/detectGeo',
  async (_, { getState }) => {
    const country = await currencyService.detectCountry();
    const { hasUserOverride } = getState().currency;
    const applied = !hasUserOverride && country ? currencyForCountry(country) : null;
    return { country, applied };
  },
);

export const refreshFxRate = createAsyncThunk(
  'currency/refreshRate',
  async () => {
    const rate = await currencyService.refreshRate();
    if (!rate) throw new Error('Failed to refresh');
    return rate;
  },
);

const currencySlice = createSlice({
  name: 'currency',
  initialState,
  reducers: {
    setDisplayCurrency(state, action: PayloadAction<CurrencyCode>) {
      state.displayCurrency = action.payload;
      state.hasUserOverride = true;
      try { localStorage.setItem(LS_KEY, action.payload); } catch { /* noop */ }
    },
    resetToDefault(state) {
      // Prefer the geo-based default when known so a Lagos user who
      // tapped USD then "reset" lands back on NGN, not on the admin default.
      const geoDefault = state.geoCountry ? currencyForCountry(state.geoCountry) : null;
      state.displayCurrency = geoDefault ?? state.adminDefault;
      state.hasUserOverride = false;
      try { localStorage.removeItem(LS_KEY); } catch { /* noop */ }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrencySettings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCurrencySettings.fulfilled, (state, action) => {
        state.adminDefault = action.payload.defaultCurrency;
        state.rate = action.payload.rate;
        // Admin default only applies if the user has no explicit override
        // AND we haven't already locked in a geo-based decision.
        if (!state.hasUserOverride && !state.geoCountry) {
          state.displayCurrency = action.payload.defaultCurrency;
        }
        state.status = 'ready';
      })
      .addCase(fetchCurrencySettings.rejected, (state) => {
        state.status = 'ready';
      })
      .addCase(updateDefaultCurrency.fulfilled, (state, action) => {
        state.adminDefault = action.payload.defaultCurrency;
        state.rate = action.payload.rate;
        if (!state.hasUserOverride && !state.geoCountry) {
          state.displayCurrency = action.payload.defaultCurrency;
        }
      })
      .addCase(refreshFxRate.fulfilled, (state, action) => {
        state.rate = action.payload;
      })
      .addCase(detectGeoCurrency.fulfilled, (state, action) => {
        state.geoCountry = action.payload.country;
        if (action.payload.applied) {
          state.displayCurrency = action.payload.applied;
        }
      });
  },
});

export const { setDisplayCurrency, resetToDefault } = currencySlice.actions;
export default currencySlice.reducer;
